import React, { useEffect, useState } from 'react'
import styled from 'styled-components';
import { userContext } from '../usercontext';
import { fetchWrapper } from '../utils/apiHandlers';
import SelectComp from '../components/SelectComp';
import MessageModal from '../components/MessageModal';
import SmallButton from '../components/SmallButton';
import Button from '../components/Button';
import UsersComp from '../components/UsersComp';
import TrainerComp from '../components/TrainerComp';
import TestChatbotComp from '../components/TestChatbotComp';
import HorizontalLine from '../components/horizontalLine';
import QuestionComp from '../components/QuestionComp';
import ShowLogsComp from '../components/ShowLogsComp';
import LeadsComp from '../components/LeadsComp';
import DirectoryComp from '../components/DirectoryComp';
import SystemComp from '../components/SystemComp';
import theme from '../theme';
import { FiMapPin, FiUsers, FiActivity, FiUserCheck, FiSmile } from "react-icons/fi";

interface Prop{
    history?: any;
}

const NAV_ITEMS = [
    { id: "directory", label: "Directory", icon: FiMapPin },
    { id: "leads", label: "Leads", icon: FiUsers },
    { id: "system", label: "System", icon: FiActivity },
    { id: "business", label: "Business Users", icon: FiUserCheck },
    { id: "shoppers", label: "Ask Amigo Shoppers", icon: FiSmile },
]

// Ask Amigo shoppers - light by design, they have no business fields to manage, just an
// account and saved chats (see api/admin.ts /getshoppers).
const ShoppersTab: React.FC = () => {
    const [ shoppers, setshoppers ] = useState<any[]>([])
    const [ loading, setloading ] = useState(false)

    const load = async () => {
        setloading(true)
        try {
            let response = await fetchWrapper.post("/api/admin/getshoppers", {})
            setshoppers(response.shoppers || [])
        } catch (error) { }
        setloading(false)
    }

    useEffect(() => { load() }, [])

    return (
        <Column style={{ alignItems: 'stretch' }}>
            <TopRow>
                <SubHeader>Ask Amigo shoppers ({shoppers.length})</SubHeader>
                <SmallButton text={loading ? "Loading…" : "Refresh"} handleClick={load} />
            </TopRow>
            <ShopperTable>
                <thead>
                    <tr><th>Email</th><th>Name</th><th>Signed up</th><th>Saved chats</th></tr>
                </thead>
                <tbody>
                    {shoppers.map((s) => (
                        <tr key={s.userid}>
                            <td>{s.email}</td>
                            <td>{s.firstname || '—'}</td>
                            <td>{new Date(s.register_date).toLocaleDateString()}</td>
                            <td>{s.chatcount}</td>
                        </tr>
                    ))}
                </tbody>
            </ShopperTable>
            {!shoppers.length && !loading && <SubHeader style={{ fontSize: 13, fontWeight: 400 }}>No shoppers have signed up yet.</SubHeader>}
        </Column>
    )
}

// The old select-a-user toolbox (user info / trainer / test model / questions / logs) - kept as
// its own tab rather than redesigned, since it wasn't part of this pass.
const UsersTab: React.FC<{ openModal: (h: string, m: string, p?: boolean) => void }> = ({ openModal }) => {
    const [ users, setusers ] = useState<any>(null)
    const [ selecteduser, setselecteduser ] = useState(-1)
    const [ emails, setemails ] = useState<any>(["--users--"])

    const [ showuserinfo, setshowuserinfo ] = useState(false)
    const [ showtrainer, setshowtrainer ] = useState(false)
    const [ showtestmodel, setshowtestmodel ] = useState(false)
    const [ showlogscomp, setshowlogscomp ] = useState(false)
    const [ showaskedquestions, setshowaskedquestions ] = useState(false)

    const loadusers = async () => {
        openModal("Loading users", "", true)
        try{
            let response = await fetchWrapper.post("/api/admin/getallusers", { accounttype: "business" })
            let emails=["--users--"]
            for(let i=0; i<response.users.length; i++){
                emails.push((response.users[i].email))
            }
            setemails(emails)
            setusers(response.users)
            openModal("", "", false)
        }catch(error){
            openModal("Error", `${error}`)
        }
    }

    const changeuser = (e:any) =>{
        setselecteduser(e.target.value)
        setshowuserinfo(false)
        setshowtrainer(false)
        setshowtestmodel(false)
        setshowlogscomp(false)
        setshowaskedquestions(false)
    }

    return (
        <Column style={{alignItems:'center'}}>
            <SubHeader># of users: {emails.length-1}</SubHeader>
            <SelectComp placeholder='--users--' labels={emails} id="select_user_id" optionid={selecteduser} onChange={(e)=>changeuser(e)}/>
            <SmallButton text="load users" handleClick={()=>loadusers()} />
            <Header style={{wordBreak:'break-all', fontSize: 20}}>
                {selecteduser>0?users[selecteduser-1].websiteurl:""}
            </Header>
            <ButtonContainer>
                <Button text="user info" handleClick={()=>{setshowuserinfo(!showuserinfo)}} backgroundColor={showuserinfo?"red":""}/>
                <Button text="Trainer Data" handleClick={()=>{setshowtrainer(!showtrainer)}} backgroundColor={showtrainer?"red":""}/>
                <Button text="Test Model" handleClick={()=>{setshowtestmodel(!showtestmodel)}} backgroundColor={showtestmodel?"red":""}/>
                <Button text="Questions" handleClick={()=>{setshowaskedquestions(!showaskedquestions)}} backgroundColor={showaskedquestions?"red":""}/>
                <Button text="show logs" handleClick={()=>{setshowlogscomp(!showlogscomp)}} backgroundColor={showlogscomp?"red":""}/>
            </ButtonContainer>

            {showuserinfo && selecteduser>0 && <><UsersComp openModal={openModal} user={users[selecteduser-1]} /><HorizontalLine /></>}
            {showtestmodel && selecteduser>0 && <><TestChatbotComp userid={users[selecteduser-1].userid} /><HorizontalLine /></>}
            {showtrainer && selecteduser>0 && <TrainerComp userid={users[selecteduser-1].userid} />}
            {showaskedquestions && selecteduser>0 && <QuestionComp userid={users[selecteduser-1].userid} />}
            {showlogscomp && <ShowLogsComp />}
        </Column>
    )
}

const AdminPage : React.FC<Prop>=(prop)=>{
    const [ showitem, setshowitem ] = useState("directory")

    const [modalheader, setmodalheader] = useState('')
    const [modalmessage, setmodalmessage] = useState('')
    const [modalprogress, setmodalprogress] = useState(false)
    const [modalstate, setmodalstate] = useState(false)

    const openModal = (header: string, message: string, progress?: boolean) => {
        setmodalheader(header)
        setmodalmessage(message)
        setmodalstate(!!header || !!message)
        setmodalprogress(progress?progress: false)
    }

    const showComp = () => {
        switch (showitem) {
            case "directory": return <DirectoryComp />
            case "leads": return <LeadsComp />
            case "system": return <SystemComp />
            case "business": return <UsersTab openModal={openModal} />
            case "shoppers": return <ShoppersTab />
            default: return <></>
        }
    }

    useEffect(()=>{
        let user: any = JSON.parse(localStorage.getItem('user')||'{}')
        if(user&&user.status==200&&user.role=="admin"){
        }else{
            userContext.logout()
            prop.history.push("/login")
        }
    }, [])

    const activeLabel = NAV_ITEMS.find((item) => item.id === showitem)?.label || "Admin"

    return(
        <PageContainer>
            <MessageModal header={modalheader} message={modalmessage} state={modalstate} handleClose={()=> setmodalstate(false)} progress={modalprogress} />
            <TopBar>
                <Greeting>Admin</Greeting>
                <SectionLabel>{activeLabel}</SectionLabel>
            </TopBar>
            <Layout>
                <Sidebar>
                    {NAV_ITEMS.map((item) => {
                        const Icon = item.icon
                        const active = showitem === item.id
                        return (
                            <NavItem key={item.id} $active={active} onClick={()=>setshowitem(item.id)}>
                                <Icon size={17} />
                                <span>{item.label}</span>
                            </NavItem>
                        )
                    })}
                </Sidebar>
                <ContentCard>
                    {showComp()}
                </ContentCard>
            </Layout>
        </PageContainer>
    );
}

const ButtonContainer = styled.div`
    display: flex;
    width: 100%;
    flex-wrap: wrap;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
`

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

const ShopperTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  th, td {
    text-align: left;
    padding: 8px 10px;
    border-bottom: 1px solid ${theme.colors.border};
  }
  th {
    font-size: 11px;
    color: ${theme.colors.textMuted};
    font-weight: 700;
  }
`

const Header = styled.div`
    padding: 5px;
    font-weight: 700;
    font-size: 30px;
    justify-content: flex-end;
    color: black;
`;

const SubHeader = styled.div`
    padding: 10px;
    font-weight: 700;
    font-size: 20px;
    justify-content: flex-end;
    color: black;
`;

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 85vh;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px 20px 60px;
  @media (max-width: 770px) {
    padding: 16px 12px 40px;
  }
`

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
`

const Greeting = styled.div`
  font-size: 24px;
  font-weight: 800;
  color: ${theme.colors.text};
`

const SectionLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.textMuted};
`

const Layout = styled.div`
  display: flex;
  gap: 20px;
  align-items: flex-start;
  @media (max-width: 770px) {
    flex-direction: column;
  }
`

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 200px;
  flex-shrink: 0;
  background: ${theme.colors.primaryDark};
  border-radius: ${theme.radius.lg};
  box-shadow: ${theme.shadow.card};
  padding: 10px;
  @media (max-width: 770px) {
    flex-direction: row;
    flex-wrap: wrap;
    width: 100%;
  }
`

const NavItem = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: ${theme.radius.md};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  user-select: none;
  color: ${(p) => (p.$active ? "#fff" : "#B9C6DE")};
  background: ${(p) => (p.$active ? "rgba(255,255,255,.12)" : "transparent")};
  transition: background 0.15s ease;
  &:hover {
    background: rgba(255,255,255,.08);
  }
`

const ContentCard = styled.div`
  flex: 1;
  min-width: 0;
  width: 100%;
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.lg};
  box-shadow: ${theme.shadow.card};
  padding: 28px;
  color: ${theme.colors.text};
  @media (max-width: 770px) {
    padding: 18px;
  }
`

export default AdminPage
